/**
 * Support/resistance zones as a Lightweight Charts series primitive.
 *
 * v5 has no built-in "rectangle", so the zones are drawn straight onto the
 * chart canvas through `ISeriesPrimitive`. This is the mechanism the plan
 * called for: full control of the drawing layer without the licensed
 * Charting Library.
 *
 * The renderer converts prices to pixels via the series' own price scale on
 * every frame, so zones stay glued to price through zoom and pan.
 */

import type {
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesApi,
  ISeriesPrimitive,
  SeriesAttachedParameter,
  SeriesType,
  Time,
} from 'lightweight-charts'

import { COLORS } from '@/theme/chart'
import type { Zone } from '@/api/types'

interface DrawTarget {
  useMediaCoordinateSpace(fn: (scope: {
    context: CanvasRenderingContext2D
    mediaSize: { width: number; height: number }
  }) => void): void
}

class ZoneRenderer implements IPrimitivePaneRenderer {
  constructor(
    private readonly bands: { top: number; bottom: number; zone: Zone }[],
    private readonly showLabels: boolean,
  ) {}

  draw(target: DrawTarget): void {
    target.useMediaCoordinateSpace(({ context, mediaSize }) => {
      // Zones can sit a few pixels apart; without this their labels stack
      // into an unreadable smear.
      let lastLabelY = Number.NEGATIVE_INFINITY

      for (const { top, bottom, zone } of this.bands) {
        const isSupport = zone.kind === 'support'
        const height = Math.max(1, bottom - top)

        context.fillStyle = isSupport ? COLORS.support : COLORS.resistance
        context.fillRect(0, top, mediaSize.width, height)

        context.strokeStyle = isSupport ? COLORS.supportBorder : COLORS.resistanceBorder
        context.lineWidth = 1
        context.beginPath()
        context.moveTo(0, top + 0.5)
        context.lineTo(mediaSize.width, top + 0.5)
        context.moveTo(0, bottom - 0.5)
        context.lineTo(mediaSize.width, bottom - 0.5)
        context.stroke()

        if (!this.showLabels) continue
        const labelY = Math.max(10, top + 11)
        if (labelY - lastLabelY < 12) continue
        lastLabelY = labelY

        // Touch count is the whole basis for a Phase 1 level, so it is what
        // the label shows -- not a generic "S/R" tag.
        const label = `${isSupport ? 'HT' : 'KC'} · ${zone.touches} chạm`
        context.font = '10px -apple-system, sans-serif'
        context.fillStyle = isSupport ? COLORS.up : COLORS.down
        context.fillText(label, 6, labelY)
      }
    })
  }
}

class ZonePaneView implements IPrimitivePaneView {
  constructor(private readonly primitive: ZonePrimitive) {}

  renderer(): IPrimitivePaneRenderer | null {
    const series = this.primitive.series
    if (!series) return null

    const bands = this.primitive.zones
      .map((zone) => {
        const high = series.priceToCoordinate(zone.price_high)
        const low = series.priceToCoordinate(zone.price_low)
        if (high === null || low === null) return null
        return { top: Math.min(high, low), bottom: Math.max(high, low), zone }
      })
      .filter((band): band is { top: number; bottom: number; zone: Zone } => band !== null)

    return new ZoneRenderer(bands, this.primitive.showLabels)
  }

  zOrder(): 'bottom' | 'normal' | 'top' {
    // Behind the candles: a zone is context, not a thing to read price off.
    return 'bottom'
  }
}

export class ZonePrimitive implements ISeriesPrimitive<Time> {
  series: ISeriesApi<SeriesType> | null = null
  zones: Zone[] = []
  showLabels = true

  private readonly views = [new ZonePaneView(this)]
  private requestUpdate?: () => void

  attached(param: SeriesAttachedParameter<Time>): void {
    this.series = param.series
    this.requestUpdate = param.requestUpdate
  }

  detached(): void {
    this.series = null
    this.requestUpdate = undefined
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return this.views
  }

  setZones(zones: Zone[], showLabels = true): void {
    this.zones = zones
    this.showLabels = showLabels
    this.requestUpdate?.()
  }
}
